export type VehicleFilterState = {
  searchTerm: string;
  selectedBrand: string;
  selectedModel: string;
  selectedVersion: string;
  selectedFuels: string[];
  selectedTransmissions: string[];
  selectedType: string;
  selectedCategory: string;
  selectedColors: string[];
  selectedOptionals: string[];
  selectedStores: string[];
  priceRange: [number, number];
  yearRange: [number, number];
  mileageRange: [number, number];
};

export type FilterRanges = {
  minPrice: number;
  maxPrice: number;
  minYear: number;
  maxYear: number;
  minMileage: number;
  maxMileage: number;
};

export function buildDefaultFilters(
  ranges: FilterRanges,
  searchParams?: URLSearchParams
): VehicleFilterState {
  const params = searchParams ?? new URLSearchParams();

  const state: VehicleFilterState = {
    searchTerm: params.get('search') || '',
    selectedBrand: params.get('brand') || 'all',
    selectedModel: params.get('model') || 'all',
    selectedVersion: params.get('version') || 'all',
    selectedFuels: params.get('fuel') ? [params.get('fuel')!] : [],
    selectedTransmissions: params.get('transmission') ? [params.get('transmission')!] : [],
    selectedType: params.get('type') || 'all',
    selectedCategory: params.get('category') || 'all',
    selectedColors: params.get('color') ? [params.get('color')!] : [],
    selectedOptionals: params.get('optionals')
      ? params.get('optionals')!.split(',').filter(Boolean)
      : [],
    selectedStores: params.get('store') ? [params.get('store')!] : [],
    priceRange: [ranges.minPrice, ranges.maxPrice],
    yearRange: [ranges.minYear, ranges.maxYear],
    mileageRange: [ranges.minMileage, ranges.maxMileage],
  };

  const minYearParam = params.get('minYear');
  const maxYearParam = params.get('maxYear');
  if (minYearParam && maxYearParam) {
    const minYear = parseInt(minYearParam, 10);
    const maxYear = parseInt(maxYearParam, 10);
    if (!isNaN(minYear) && !isNaN(maxYear)) {
      state.yearRange = [minYear, maxYear];
    }
  }

  const minPriceParam = params.get('minPrice');
  const maxPriceParam = params.get('maxPrice');
  if (minPriceParam && maxPriceParam) {
    const minPrice = parseInt(minPriceParam, 10);
    const maxPrice = parseInt(maxPriceParam, 10);
    if (!isNaN(minPrice) && !isNaN(maxPrice)) {
      state.priceRange = [minPrice, maxPrice];
    }
  }

  const minMileageParam = params.get('minMileage');
  const maxMileageParam = params.get('maxMileage');
  if (minMileageParam && maxMileageParam) {
    const minMileage = parseInt(minMileageParam, 10);
    const maxMileage = parseInt(maxMileageParam, 10);
    if (!isNaN(minMileage) && !isNaN(maxMileage)) {
      state.mileageRange = [minMileage, maxMileage];
    }
  }

  return state;
}

export function filtersToSearchParams(filters: VehicleFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.searchTerm.trim()) params.set('search', filters.searchTerm.trim());
  if (filters.selectedBrand !== 'all') params.set('brand', filters.selectedBrand);
  if (filters.selectedModel !== 'all') params.set('model', filters.selectedModel);
  if (filters.selectedVersion && filters.selectedVersion !== 'all') {
    params.set('version', filters.selectedVersion);
  }
  if (filters.selectedType !== 'all') params.set('type', filters.selectedType);
  if (filters.selectedCategory !== 'all') params.set('category', filters.selectedCategory);
  if (filters.selectedFuels.length === 1) params.set('fuel', filters.selectedFuels[0]);
  if (filters.selectedTransmissions.length === 1) {
    params.set('transmission', filters.selectedTransmissions[0]);
  }
  if (filters.selectedColors.length === 1) params.set('color', filters.selectedColors[0]);
  if (filters.selectedStores.length === 1) params.set('store', filters.selectedStores[0]);
  if (filters.selectedOptionals.length > 0) {
    params.set('optionals', filters.selectedOptionals.join(','));
  }
  params.set('minPrice', String(filters.priceRange[0]));
  params.set('maxPrice', String(filters.priceRange[1]));
  params.set('minYear', String(filters.yearRange[0]));
  params.set('maxYear', String(filters.yearRange[1]));
  params.set('minMileage', String(filters.mileageRange[0]));
  params.set('maxMileage', String(filters.mileageRange[1]));
  return params;
}

export function hasActiveFilters(filters: VehicleFilterState, ranges: FilterRanges): boolean {
  return (
    !!filters.searchTerm ||
    filters.selectedBrand !== 'all' ||
    filters.selectedModel !== 'all' ||
    (filters.selectedVersion && filters.selectedVersion !== 'all') ||
    filters.selectedFuels.length > 0 ||
    filters.selectedTransmissions.length > 0 ||
    filters.selectedType !== 'all' ||
    filters.selectedCategory !== 'all' ||
    filters.selectedColors.length > 0 ||
    filters.selectedOptionals.length > 0 ||
    filters.selectedStores.length > 0 ||
    filters.priceRange[0] > ranges.minPrice ||
    filters.priceRange[1] < ranges.maxPrice ||
    filters.yearRange[0] > ranges.minYear ||
    filters.yearRange[1] < ranges.maxYear ||
    filters.mileageRange[0] > ranges.minMileage ||
    filters.mileageRange[1] < ranges.maxMileage
  );
}

export function hasAdvancedFilters(filters: VehicleFilterState, ranges: FilterRanges): boolean {
  return (
    !!filters.searchTerm ||
    filters.selectedFuels.length > 0 ||
    filters.selectedTransmissions.length > 0 ||
    filters.selectedCategory !== 'all' ||
    filters.selectedColors.length > 0 ||
    filters.selectedOptionals.length > 0 ||
    filters.selectedStores.length > 0 ||
    filters.priceRange[0] > ranges.minPrice ||
    filters.priceRange[1] < ranges.maxPrice ||
    filters.yearRange[0] > ranges.minYear ||
    filters.yearRange[1] < ranges.maxYear ||
    filters.mileageRange[0] > ranges.minMileage ||
    filters.mileageRange[1] < ranges.maxMileage
  );
}

export function filterVehicles(
  contextVehicles: any[],
  filters: VehicleFilterState,
  searchParams: URLSearchParams,
  ranges: FilterRanges
): any[] {
  const idsParam = searchParams.get('ids');
  if (idsParam) {
    const vehicleIds = idsParam.split(',');
    return contextVehicles.filter((vehicle) => vehicleIds.includes(vehicle.id.toString()));
  }

  const urlFuel = searchParams.get('fuel');
  const urlTransmission = searchParams.get('transmission');
  const urlColor = searchParams.get('color');

  const activeFuels = urlFuel ? [urlFuel] : filters.selectedFuels;
  const activeTransmissions = urlTransmission ? [urlTransmission] : filters.selectedTransmissions;
  const activeColors = urlColor ? [urlColor] : filters.selectedColors;

  return contextVehicles.filter((vehicle) => {
    const matchesSearch =
      filters.searchTerm === '' ||
      `${vehicle.brand} ${vehicle.model} ${vehicle.version}`
        .toLowerCase()
        .includes(filters.searchTerm.toLowerCase());

    const matchesBrand = filters.selectedBrand === 'all' || vehicle.brand === filters.selectedBrand;
    const matchesModel = filters.selectedModel === 'all' || vehicle.model === filters.selectedModel;
    const matchesVersion =
      !filters.selectedVersion ||
      filters.selectedVersion === 'all' ||
      vehicle.version === filters.selectedVersion;
    const matchesFuels = activeFuels.length === 0 || activeFuels.includes(vehicle.fuel);
    const matchesTransmissions =
      activeTransmissions.length === 0 || activeTransmissions.includes(vehicle.transmission);
    const matchesType =
      filters.selectedType === 'all' ||
      (filters.selectedType === 'zerokm' && (vehicle.isZeroKm || vehicle.mileage == 0)) ||
      (filters.selectedType === 'seminovo' && !vehicle.isZeroKm && vehicle.mileage > 0);
    const matchesCategory =
      filters.selectedCategory === 'all' || vehicle.type.toString() === filters.selectedCategory;
    const matchesColors = activeColors.length === 0 || activeColors.includes(vehicle.color);
    const matchesOptionals =
      filters.selectedOptionals.length === 0 ||
      filters.selectedOptionals.every((optional) => vehicle.optionals?.includes(optional));
    const matchesStores =
      filters.selectedStores.length === 0 ||
      (vehicle.store && filters.selectedStores.includes(vehicle.store));
    const matchesPrice =
      vehicle.price >= filters.priceRange[0] && vehicle.price <= filters.priceRange[1];
    const matchesYear =
      filters.yearRange[0] === filters.yearRange[1]
        ? vehicle.modelYear === filters.yearRange[0]
        : vehicle.manufactureYear >= filters.yearRange[0] &&
          vehicle.modelYear <= filters.yearRange[1];
    const matchesMileage =
      vehicle.mileage >= filters.mileageRange[0] && vehicle.mileage <= filters.mileageRange[1];

    return (
      matchesSearch &&
      matchesBrand &&
      matchesModel &&
      matchesVersion &&
      matchesFuels &&
      matchesTransmissions &&
      matchesType &&
      matchesCategory &&
      matchesColors &&
      matchesOptionals &&
      matchesStores &&
      matchesPrice &&
      matchesYear &&
      matchesMileage
    );
  });
}

export function sortVehicles(vehicles: any[], sortBy: string): any[] {
  const sorted = [...vehicles];
  switch (sortBy) {
    case 'price-asc':
      sorted.sort((a, b) => a.price - b.price);
      break;
    case 'price-desc':
      sorted.sort((a, b) => b.price - a.price);
      break;
    case 'year-desc':
      sorted.sort((a, b) => b.modelYear - a.modelYear);
      break;
    case 'year-asc':
      sorted.sort((a, b) => a.modelYear - b.modelYear);
      break;
    case 'mileage-asc':
      sorted.sort((a, b) => a.mileage - b.mileage);
      break;
    case 'brand':
      sorted.sort((a, b) => a.brand.localeCompare(b.brand));
      break;
    default:
      break;
  }
  return sorted;
}

/** Dimensões que podem ser ignoradas ao calcular opções de um facet (filtros dependentes). */
export type FilterFacet =
  | 'search'
  | 'brand'
  | 'model'
  | 'type'
  | 'category'
  | 'fuels'
  | 'transmissions'
  | 'colors'
  | 'optionals'
  | 'stores'
  | 'price'
  | 'year'
  | 'mileage';

const EMPTY_PARAMS = new URLSearchParams();

/**
 * Veículos disponíveis para montar opções de um facet:
 * aplica todos os filtros atuais, exceto a(s) dimensão(ões) pedida(s).
 * Ex.: brands → exclui brand+model; models → exclui só model.
 */
export function vehiclesForFacet(
  contextVehicles: any[],
  filters: VehicleFilterState,
  ranges: FilterRanges,
  exclude: FilterFacet | FilterFacet[],
  searchParams: URLSearchParams = EMPTY_PARAMS
): any[] {
  const excludes = new Set(Array.isArray(exclude) ? exclude : [exclude]);
  const facetFilters: VehicleFilterState = {
    ...filters,
    searchTerm: excludes.has('search') ? '' : filters.searchTerm,
    selectedBrand: excludes.has('brand') ? 'all' : filters.selectedBrand,
    selectedModel: excludes.has('model') ? 'all' : filters.selectedModel,
    selectedType: excludes.has('type') ? 'all' : filters.selectedType,
    selectedCategory: excludes.has('category') ? 'all' : filters.selectedCategory,
    selectedFuels: excludes.has('fuels') ? [] : filters.selectedFuels,
    selectedTransmissions: excludes.has('transmissions') ? [] : filters.selectedTransmissions,
    selectedColors: excludes.has('colors') ? [] : filters.selectedColors,
    selectedOptionals: excludes.has('optionals') ? [] : filters.selectedOptionals,
    selectedStores: excludes.has('stores') ? [] : filters.selectedStores,
    priceRange: excludes.has('price')
      ? [ranges.minPrice, ranges.maxPrice]
      : filters.priceRange,
    yearRange: excludes.has('year') ? [ranges.minYear, ranges.maxYear] : filters.yearRange,
    mileageRange: excludes.has('mileage')
      ? [ranges.minMileage, ranges.maxMileage]
      : filters.mileageRange,
  };

  // Facets usam só o estado atual — não reintroduzir fuel/color/transmission da URL
  const facetParams = new URLSearchParams(searchParams);
  facetParams.delete('fuel');
  facetParams.delete('transmission');
  facetParams.delete('color');
  facetParams.delete('ids');

  return filterVehicles(contextVehicles, facetFilters, facetParams, ranges);
}

export function uniqueSorted(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((v): v is string => !!v && v.trim() !== ''))).sort((a, b) =>
    a.localeCompare(b, 'pt-BR')
  );
}

/** Opções de facet a partir do estado atual (para sanitizar URL / apply). */
export function buildFacetOptions(
  contextVehicles: any[],
  filters: VehicleFilterState,
  ranges: FilterRanges,
  searchParams: URLSearchParams = EMPTY_PARAMS
): {
  brands: string[];
  models: string[];
  fuels: string[];
  transmissions: string[];
  colors: string[];
  optionals: string[];
  categories: string[];
  types: string[];
} {
  const typePool = vehiclesForFacet(contextVehicles, filters, ranges, 'type', searchParams);
  const types: string[] = [];
  if (typePool.some((v) => v.isZeroKm || v.mileage === 0)) types.push('zerokm');
  if (typePool.some((v) => !v.isZeroKm && v.mileage > 0)) types.push('seminovo');

  const categoryPool = vehiclesForFacet(contextVehicles, filters, ranges, 'category', searchParams);

  return {
    brands: uniqueSorted(
      vehiclesForFacet(contextVehicles, filters, ranges, ['brand', 'model'], searchParams).map(
        (v) => v.brand
      )
    ),
    models: uniqueSorted(
      vehiclesForFacet(contextVehicles, filters, ranges, 'model', searchParams).map((v) => v.model)
    ),
    fuels: uniqueSorted(
      vehiclesForFacet(contextVehicles, filters, ranges, 'fuels', searchParams).map((v) => v.fuel)
    ),
    transmissions: uniqueSorted(
      vehiclesForFacet(contextVehicles, filters, ranges, 'transmissions', searchParams).map(
        (v) => v.transmission
      )
    ),
    colors: uniqueSorted(
      vehiclesForFacet(contextVehicles, filters, ranges, 'colors', searchParams).map((v) => v.color)
    ),
    optionals: uniqueSorted(
      vehiclesForFacet(contextVehicles, filters, ranges, 'optionals', searchParams).flatMap(
        (v) => v.optionals || []
      )
    ),
    categories: uniqueSorted(categoryPool.map((v) => String(v.type))),
    types,
  };
}

/** Remove seleções que não existem mais no pool de opções dependentes. */
export function sanitizeFilterSelections(
  filters: VehicleFilterState,
  options: {
    brands: string[];
    models: string[];
    fuels: string[];
    transmissions: string[];
    colors: string[];
    optionals: string[];
    categories: string[];
    types: string[];
  }
): VehicleFilterState {
  let next = { ...filters };
  let brandCleared = false;

  if (next.selectedBrand !== 'all' && !options.brands.includes(next.selectedBrand)) {
    next = { ...next, selectedBrand: 'all', selectedModel: 'all', selectedVersion: 'all' };
    brandCleared = true;
  }

  if (
    !brandCleared &&
    next.selectedModel !== 'all' &&
    !options.models.includes(next.selectedModel)
  ) {
    next = { ...next, selectedModel: 'all', selectedVersion: 'all' };
  }

  if (next.selectedType !== 'all' && !options.types.includes(next.selectedType)) {
    next = { ...next, selectedType: 'all' };
  }

  if (next.selectedCategory !== 'all' && !options.categories.includes(next.selectedCategory)) {
    next = { ...next, selectedCategory: 'all' };
  }

  const fuels = next.selectedFuels.filter((f) => options.fuels.includes(f));
  const transmissions = next.selectedTransmissions.filter((t) => options.transmissions.includes(t));
  const colors = next.selectedColors.filter((c) => options.colors.includes(c));
  const optionals = next.selectedOptionals.filter((o) => options.optionals.includes(o));

  if (fuels.length !== next.selectedFuels.length) next = { ...next, selectedFuels: fuels };
  if (transmissions.length !== next.selectedTransmissions.length) {
    next = { ...next, selectedTransmissions: transmissions };
  }
  if (colors.length !== next.selectedColors.length) next = { ...next, selectedColors: colors };
  if (optionals.length !== next.selectedOptionals.length) {
    next = { ...next, selectedOptionals: optionals };
  }

  return next;
}
